/**
 * Escalation Banner Component
 * ============================
 * Displays escalation status and provides actions for escalated conversations
 */

import React, { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Stack,
  Typography,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  Warning,
  Person,
  Schedule,
  Close,
  SmartToy,
  CheckCircle,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import api from '../../services/api';

interface EscalationBannerProps {
  conversationId: number;
  escalated: boolean;
  escalationReason?: string;
  escalationTrigger?: string;
  escalatedAt?: Date;
  escalatedBy?: string;
  assignedAgent?: {
    id: number;
    first_name: string;
    last_name: string;
    full_name: string;
    avatar_url?: string;
  };
  onResolve?: () => void;
  onToggleAI?: () => void;
}

export const EscalationBanner: React.FC<EscalationBannerProps> = ({
  conversationId,
  escalated,
  escalationReason,
  escalationTrigger,
  escalatedAt,
  escalatedBy,
  assignedAgent,
  onResolve,
  onToggleAI
}) => {
  const [expanded, setExpanded] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolveNote, setResolveNote] = useState('');
  const [loading, setLoading] = useState(false);

  if (!escalated) {
    return null;
  }

  // Get trigger display info
  const getTriggerInfo = (trigger?: string) => {
    const triggerMap: Record<string, { label: string; color: 'error' | 'warning' | 'info' }> = {
      explicit_request: { label: 'Human Requested', color: 'info' },
      confidence: { label: 'Low Confidence', color: 'warning' },
      sentiment: { label: 'Negative Sentiment', color: 'error' },
      repetition: { label: 'Repetitive Responses', color: 'warning' },
      interaction_count: { label: 'Max Interactions', color: 'info' }
    };

    return triggerMap[trigger || ''] || { label: trigger || 'Unknown', color: 'warning' };
  };

  const triggerInfo = getTriggerInfo(escalationTrigger);

  // Handle resolve escalation
  const handleResolve = async () => {
    try {
      setLoading(true);
      await api.post(`/conversations/${conversationId}/resolve-escalation`, {
        resolution_note: resolveNote
      });
      setResolveDialogOpen(false);
      setResolveNote('');
      if (onResolve) onResolve();
    } catch (error) {
      console.error('Failed to resolve escalation:', error);
      alert('Failed to resolve escalation');
    } finally {
      setLoading(false);
    }
  };

  // Handle re-enable AI
  const handleEnableAI = async () => {
    try {
      setLoading(true);
      await api.put(`/conversations/${conversationId}/ai-toggle`, {
        ai_enabled: true
      });
      if (onToggleAI) onToggleAI();
    } catch (error) {
      console.error('Failed to enable AI:', error);
      alert('Failed to enable AI');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Alert
        severity="warning"
        icon={<Warning />}
        sx={{ mb: 2, borderRadius: 2 }}
        action={
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        }
      >
        <AlertTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          Conversation Escalated to Human Agent
          <Chip
            label={triggerInfo.label}
            size="small"
            color={triggerInfo.color}
            sx={{ height: 20 }}
          />
        </AlertTitle>

        <Typography variant="body2" sx={{ mb: 1 }}>
          {escalationReason || 'This conversation requires human assistance'}
        </Typography>

        {/* Quick actions */}
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<CheckCircle />}
            onClick={() => setResolveDialogOpen(true)}
            disabled={loading}
          >
            Resolve
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<SmartToy />}
            onClick={handleEnableAI}
            disabled={loading}
          >
            Re-enable AI
          </Button>
        </Stack>

        {/* Expanded details */}
        <Collapse in={expanded}>
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Stack spacing={1}>
              {/* Assigned agent */}
              {assignedAgent && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Person fontSize="small" />
                  <Typography variant="body2">
                    <strong>Assigned to:</strong> {assignedAgent.full_name}
                  </Typography>
                </Stack>
              )}

              {/* Escalation time */}
              {escalatedAt && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Schedule fontSize="small" />
                  <Typography variant="body2">
                    <strong>Escalated:</strong> {escalatedAt.toLocaleString()}
                  </Typography>
                </Stack>
              )}

              {/* Escalated by */}
              {escalatedBy && (
                <Typography variant="body2">
                  <strong>Escalated by:</strong> {escalatedBy}
                </Typography>
              )}

              {/* Trigger type */}
              <Typography variant="body2">
                <strong>Trigger:</strong> {triggerInfo.label}
              </Typography>
            </Stack>
          </Box>
        </Collapse>
      </Alert>

      {/* Resolve Dialog */}
      <Dialog
        open={resolveDialogOpen}
        onClose={() => setResolveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircle color="success" />
            <span>Resolve Escalation</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Resolving this escalation will mark it as handled. The conversation will remain
            with the assigned human agent unless you re-enable AI.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Resolution Note (Optional)"
            placeholder="Add notes about how this escalation was resolved..."
            value={resolveNote}
            onChange={(e) => setResolveNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleResolve}
            disabled={loading}
            startIcon={<CheckCircle />}
          >
            {loading ? 'Resolving...' : 'Resolve Escalation'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EscalationBanner;
